/**
 * workflowModuleListApi.js
 *
 * Concrete instances of the generic two-API merge engine (mergedListApi.js):
 * workflow records enriched with their full module records, served in the
 * shape ListView already consumes.
 *
 * For the next "merge API X + API Y into ListView" requirement, do NOT write
 * a new merge implementation — write a small config like these against
 * createMergedListFetcher().
 *
 * Usage with the existing ListView component:
 *
 *   const fetchData = useMemo(
 *     () => createWorkflowStageListFetcher(workflowId, stageName),
 *     [workflowId, stageName],
 *   );
 *   <ListView moduleName="candidate" fetchData={fetchData} />
 */

import {
  getWorkflowStage,
  extractStageCandidates,
  getModuleRecordById,
  getAllWorkflows,
} from './workflowApi';
import { getModuleDataList } from './dropdownApi';
import { mapListResponse } from './responseMapping/mapListResponse';
import { createMergedListFetcher, defaultMergeRow, resolveMergeKey } from './mergedListApi';

export { resolveMergeKey };

/**
 * Builds a ListView-compatible fetchData function bound to one workflow stage.
 *
 * Legacy full-set mode: the stage endpoint returns the whole stage data array
 * (no server paging exists for it), so the engine paginates it client-side.
 *
 * @param {string} workflowId
 * @param {string} stageName
 * @param {object} [config]
 * @param {number} [config.moduleFetchLimit=200] bulk /module-data-list fetch
 *   size for merging; uncovered entries fall back to a per-id lookup.
 */
export function createWorkflowStageListFetcher(workflowId, stageName, config = {}) {
  return createMergedListFetcher({
    fetchPrimary: () => getWorkflowStage(workflowId, stageName),
    extractPrimaryRows: extractStageCandidates,
    fetchSecondary: getModuleDataList,
    fetchSecondaryById: getModuleRecordById,
    secondaryFetchLimit: config.moduleFetchLimit ?? 200,
  });
}

// "Pipeline" -> pipelineCount, "Client Submitted" -> clientSubmittedCount.
export function stageCountKey(stageName) {
  const words = String(stageName ?? '').trim().split(/[^a-zA-Z0-9]+/).filter(Boolean);
  if (!words.length) return '';
  const camel = words
    .map((word, i) => (i === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1).toLowerCase()))
    .join('');
  return `${camel}Count`;
}

// Workflow entries carry per-stage counts as a nested array
// (stages: [{ stageName, count }]) which ListView columns can't read —
// flatten each stage onto the row as <stageName>Count.
function withStageCounts(workflow) {
  const stages = Array.isArray(workflow?.stages) ? workflow.stages : [];
  if (!stages.length) return workflow;
  const counts = {};
  for (const stage of stages) {
    const key = stageCountKey(stage?.stageName);
    if (key) counts[key] = Number(stage?.count) || 0;
  }
  return { ...counts, ...workflow };
}

// "1 of 2" — submitted-so-far (workflow side) against the job record's
// admin-entered target. Blank when the job has no positive target set.
const readTargetSubmission = (row) => {
  const target = Number(row?.targetSubmission ?? row?.targetSubmissions);
  return Number.isFinite(target) && target > 0 ? target : null;
};

function createTargetSubmissionMergeRow({
  submittedCountField = 'submittedCount',
  progressField = 'targetSubmissionProgress',
} = {}) {
  return function mergeJobWorkflowRow(primaryRow, secondaryRow, key) {
    const merged = defaultMergeRow(primaryRow, secondaryRow, key);
    const target = readTargetSubmission(merged);
    if (target != null) {
      merged[progressField] = `${Number(merged?.[submittedCountField]) || 0} of ${target}`;
    }
    return merged;
  };
}

/**
 * Builds a ListView-compatible fetchData function for the merged module +
 * workflow list (e.g. the Jobs page): module record _id <- workflow.entityId.
 *
 * PRIMARY: /module-data-list — drives membership, order and the pager, and
 * receives ListView's options untouched, so tabs (My Jobs / All Jobs),
 * scope, field filters, sort and search all keep working exactly as on a
 * plain module list.
 * SECONDARY: /workflow/list/all — each job page is enriched with the matching
 * workflow page (stage counts flattened to <stage>Count, plus workflowId and
 * the computed targetSubmissionProgress).
 *
 * @param {object} [config]
 * @param {object[]} [config.extraFields] static ListView column configs
 *   appended after the admin-configured fields.
 * @param {string|string[]} [config.extraFieldsAfter] admin field anchor used
 *   to place the extra fields.
 */
export function createAllWorkflowsListFetcher(config = {}) {
  const mergeTargetSubmission = createTargetSubmissionMergeRow();

  const fetchMergedList = createMergedListFetcher({
    primaryKey: '_id',
    extraFields: config.extraFields,
    extraFieldsAfter: config.extraFieldsAfter,
    fetchPrimaryPage: getModuleDataList,
    joinSources: [
      {
        key: 'entityId',
        fetchByKeys: (_moduleName, _keys, _options, context) => {
          const pageSize = Math.max(1, Number(context?.limit) || 10);
          const page = Math.floor((Number(context?.offset) || 0) / pageSize) + 1;
          return getAllWorkflows(page, pageSize);
        },
        extractRows: (response) => (response?.items ?? []).map(withStageCounts),
        mergeRow: (primaryRow, secondaryRow, key) => {
          const merged = mergeTargetSubmission(primaryRow, secondaryRow, key);
          if (secondaryRow?._id) merged.workflowId = secondaryRow._id;
          return merged;
        },
      },
    ],
  });

  return fetchMergedList;
}

/**
 * One-shot convenience wrapper: merged workflows + module records in the
 * ListView-shaped model ({ rows, total, fields, ... }).
 */
export async function getAllWorkflowsModuleList(
  moduleName,
  limit = 10,
  offset = 0,
  options = {},
  config = {},
) {
  const fetchList = createAllWorkflowsListFetcher(config);
  const response = await fetchList(moduleName, limit, offset, options);
  return mapListResponse(response, moduleName);
}

/**
 * One-shot convenience wrapper for callers outside ListView that just want
 * the merged, ListView-shaped model ({ rows, total, fields, ... }).
 */
export async function getWorkflowStageModuleList(
  workflowId,
  stageName,
  moduleName,
  limit = 10,
  offset = 0,
  options = {},
) {
  const fetchList = createWorkflowStageListFetcher(workflowId, stageName);
  const response = await fetchList(moduleName, limit, offset, options);
  return mapListResponse(response, moduleName);
}
